import { useEffect, useState } from 'react';
import type { DashboardStats, StatsRange } from '../types';
import { getDashboardStats } from '../lib/api';
import { IconBuilding, IconUser, IconNews, IconChart, IconRefresh } from '../components/icons';
import { relativeDate } from '../lib/format';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../lib/toast';
import type { FoundersPreFilter } from './Founders';

interface DashboardPageProps {
  onDrillToFounders?: (filter: FoundersPreFilter) => void;
}

export function DashboardPage({ onDrillToFounders }: DashboardPageProps = {}) {
  const [range, setRange] = useState<StatsRange>('30d');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  async function refresh(r: StatsRange = range) {
    setLoading(true);
    setError(null);
    try {
      const res = await getDashboardStats(r);
      setStats(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load stats';
      setError(msg);
      toast.error('Failed to load stats', msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(range); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

  return (
    <section className="px-4 md:px-8 py-5 md:py-7 max-w-[1320px]">
      <div className="flex items-start md:items-end justify-between mb-5 md:mb-6 gap-3">
        <div className="min-w-0">
          <h1 className="h-display text-[22px] md:text-[26px]">Dashboard</h1>
          <p className="text-[12.5px] md:text-[13.5px] text-ink-muted mt-0.5 hidden sm:block">Snapshot of activity across the directory and the founder funnel.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RangeSwitcher value={range} setValue={setRange} />
          <button onClick={() => refresh(range)} className="h-9 w-9 grid place-items-center rounded-md border border-line bg-white active:bg-cream-tint" title="Refresh">
            <IconRefresh className={'w-4 h-4 text-ink-muted ' + (loading ? 'animate-spin' : '')} />
          </button>
        </div>
      </div>

      {error && <div className="mb-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-[13px]">{error}</div>}

      {/* KPI grid: 2x2 on mobile, 4 cols on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-5 md:mb-6">
        <Kpi
          label="Active funds"
          value={stats?.kpis.activeFunds ?? '-'}
          accent="funds"
          icon={<IconBuilding className="w-3.5 h-3.5" />}
          hint="from /api/admin/funds"
        />
        <Kpi
          label="Founder leads"
          value={stats?.kpis.founderLeads ?? '-'}
          accent="founders"
          icon={<IconUser className="w-3.5 h-3.5" />}
          hint={stats ? `${stats.signupsByDay.reduce((a, b) => a + b.count, 0)} new in ${range}` : ''}
        />
        <Kpi
          label="Audits completed"
          value={stats?.kpis.auditsCompleted ?? '-'}
          accent="amber"
          icon={<IconChart className="w-3.5 h-3.5" />}
          hint={stats && stats.kpis.founderLeads
            ? `${((stats.kpis.auditsCompleted / stats.kpis.founderLeads) * 100).toFixed(1)}% conv.`
            : ''}
        />
        <Kpi
          label="News pending review"
          value={stats?.kpis.newsPending ?? '-'}
          accent="navy"
          icon={<IconNews className="w-3.5 h-3.5" />}
          hint="awaiting moderation"
        />
      </div>

      {/* Charts row: stack on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-5 md:mb-6">
        <SignupsChart data={stats?.signupsByDay ?? []} loading={loading} range={range} />
        <SectorsChart data={stats?.topSectors ?? []} loading={loading} />
      </div>

      {/* Funnel + activity: stack on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <Funnel funnel={stats?.funnel} loading={loading} onDrill={onDrillToFounders} />
        <ActivityFeed activity={stats?.activity ?? []} loading={loading} />
      </div>
    </section>
  );
}

/* ---------- Range switcher ---------- */

function RangeSwitcher({ value, setValue }: { value: StatsRange; setValue: (v: StatsRange) => void }) {
  const options: StatsRange[] = ['7d', '30d', '90d', 'all'];
  return (
    <div className="inline-flex border border-line rounded-md bg-white p-0.5 text-[12.5px]">
      {options.map(o => (
        <button
          key={o}
          onClick={() => setValue(o)}
          className={'px-2.5 py-1 rounded ' + (value === o ? 'bg-cream-tint text-ink font-medium' : 'text-ink-muted hover:text-ink')}
        >{o === 'all' ? 'All' : o}</button>
      ))}
    </div>
  );
}

/* ---------- KPI card ---------- */

function Kpi({ label, value, accent, icon, hint }: {
  label: string; value: number | string; accent: 'funds' | 'founders' | 'amber' | 'navy';
  icon: React.ReactNode; hint?: string;
}) {
  const accentClasses = {
    funds: 'bg-funds-soft text-funds-deep',
    founders: 'bg-founders-soft text-founders-deep',
    amber: 'bg-amber-soft text-amber',
    navy: 'bg-cream-deep text-navy',
  }[accent];
  // Show a skeleton block while the value is still the "-" placeholder so
  // the dashboard feels alive instead of frozen during stats fetch.
  const isPlaceholder = value === '-' || value === '—';
  return (
    <div className="bg-white rounded-lg border border-line shadow-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11.5px] uppercase tracking-wider text-ink-muted font-medium">{label}</span>
        <span className={'w-7 h-7 rounded-md grid place-items-center ' + accentClasses}>{icon}</span>
      </div>
      {isPlaceholder ? <Skeleton className="h-7 w-20" /> : <div className="num text-[28px] h-display leading-none">{value}</div>}
      {hint ? <div className="mt-2.5 text-[12px] text-ink-faint">{hint}</div> : isPlaceholder ? <Skeleton className="mt-2.5 h-3 w-32" /> : null}
    </div>
  );
}

/* ---------- Signups chart (sparkline area) ---------- */

function SignupsChart({ data, loading, range }: { data: { date: string; count: number }[]; loading: boolean; range: StatsRange }) {
  const total = data.reduce((a, b) => a + b.count, 0);
  const max = Math.max(1, ...data.map(d => d.count));
  const width = 720;
  const height = 180;

  // Build polyline points and area path.
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = i * stepX;
    const y = height - 20 - ((d.count / max) * (height - 40));
    return [x, y] as const;
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = points.length
    ? `${linePath} L${points[points.length - 1][0].toFixed(1)},${height} L0,${height} Z`
    : '';

  const last = points.length > 0 ? points[points.length - 1] : null;

  // Pick a few date labels to show under the chart.
  const labels = data.length > 0
    ? [data[0]?.date, data[Math.floor(data.length / 4)]?.date, data[Math.floor(data.length / 2)]?.date, data[Math.floor((3 * data.length) / 4)]?.date, data[data.length - 1]?.date]
        .filter(Boolean)
    : [];

  return (
    <div className="col-span-2 bg-white rounded-lg border border-line shadow-card p-5">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="h-display text-[15px]">Founder signups</h3>
          <p className="text-[12.5px] text-ink-muted mt-0.5">Daily new FounderProfiles, last {range === 'all' ? '365 days' : range}</p>
        </div>
        <div className="text-right">
          <div className="num h-display text-[20px]">{loading ? '…' : total}</div>
          <div className="text-[11.5px] text-ink-muted">total in range</div>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[180px] mt-2">
        <defs>
          <linearGradient id="grad-founders" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g stroke="#EEEAE1" strokeWidth="1">
          <line x1="0" y1="20" x2={width} y2="20" />
          <line x1="0" y1="60" x2={width} y2="60" />
          <line x1="0" y1="100" x2={width} y2="100" />
          <line x1="0" y1="140" x2={width} y2="140" />
        </g>
        {areaPath && <path d={areaPath} fill="url(#grad-founders)" />}
        {linePath && <path d={linePath} stroke="#8B5CF6" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
        {last && (
          <>
            <circle cx={last[0]} cy={last[1]} r="8" fill="#8B5CF6" fillOpacity="0.18" />
            <circle cx={last[0]} cy={last[1]} r="4" fill="#8B5CF6" />
          </>
        )}
      </svg>
      <div className="flex justify-between text-[11px] text-ink-faint mt-2 font-mono">
        {labels.map((l, i) => <span key={i}>{l}</span>)}
      </div>
    </div>
  );
}

/* ---------- Top sectors chart (horizontal bars) ---------- */

function SectorsChart({ data, loading }: { data: { sector: string; count: number }[]; loading: boolean }) {
  const max = Math.max(1, ...data.map(d => d.count));
  return (
    <div className="bg-white rounded-lg border border-line shadow-card p-5">
      <div className="mb-3">
        <h3 className="h-display text-[15px]">Top sectors searched</h3>
        <p className="text-[12.5px] text-ink-muted mt-0.5">By founder profile selections</p>
      </div>
      <div className="space-y-3">
        {loading && !data.length && <div className="text-ink-muted text-[12.5px]">Loading…</div>}
        {!loading && !data.length && <div className="text-ink-muted text-[12.5px]">No data yet.</div>}
        {data.map(d => (
          <div key={d.sector}>
            <div className="flex justify-between text-[12.5px] mb-1">
              <span className="truncate">{d.sector}</span>
              <span className="num text-ink-muted">{d.count}</span>
            </div>
            <div className="h-1.5 bg-cream-deep rounded-full overflow-hidden">
              <div className="h-full bg-funds rounded-full" style={{ width: `${(d.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Funnel ---------- */

function Funnel({ funnel, loading, onDrill }: { funnel: DashboardStats['funnel'] | undefined; loading: boolean; onDrill?: (filter: FoundersPreFilter) => void }) {
  const stages: { label: string; value: number; color: string; kind: FoundersPreFilter['kind'] }[] = [
    { label: 'Visit',              value: funnel?.visit ?? 0,            color: 'border-line',     kind: 'visit' },
    { label: 'Profile started',    value: funnel?.profileStarted ?? 0,   color: 'border-founders', kind: 'profileStarted' },
    { label: 'Profile completed',  value: funnel?.profileCompleted ?? 0, color: 'border-founders', kind: 'profileCompleted' },
    { label: 'Searched funds',     value: funnel?.searched ?? 0,         color: 'border-funds',    kind: 'searched' },
    { label: 'Audit completed',    value: funnel?.audit ?? 0,            color: 'border-amber',    kind: 'auditCompleted' },
  ];
  const top = stages[0]?.value || 1;
  return (
    <div className="md:col-span-2 bg-white rounded-lg border border-line shadow-card p-4 md:p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="h-display text-[15px]">Founder funnel</h3>
          <p className="text-[12.5px] text-ink-muted mt-0.5">Progression from first visit to audit completion. Tap a stage to drill into Founders.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3">
        {stages.map((s) => (
          <button
            key={s.label}
            onClick={() => onDrill?.({ kind: s.kind })}
            disabled={!onDrill}
            className={`text-left border-l-2 ${s.color} pl-3 py-1 rounded-r transition hover:bg-cream-tint disabled:cursor-default disabled:hover:bg-transparent`}
          >
            <div className="text-[11.5px] uppercase text-ink-muted tracking-wider">{s.label}</div>
            <div className="num h-display text-[22px] mt-1">{loading ? '…' : s.value.toLocaleString()}</div>
            <div className="text-[12px] text-ink-faint">{loading ? '' : `${((s.value / top) * 100).toFixed(1)}%`}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Activity feed ---------- */

function ActivityFeed({ activity, loading }: { activity: DashboardStats['activity']; loading: boolean }) {
  const dotFor: Record<string, string> = {
    audit: 'bg-amber',
    founder: 'bg-founders',
    news: 'bg-navy',
  };
  return (
    <div className="bg-white rounded-lg border border-line shadow-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="h-display text-[15px]">Recent activity</h3>
      </div>
      {loading && !activity.length && <div className="text-ink-muted text-[12.5px]">Loading…</div>}
      {!loading && !activity.length && <div className="text-ink-muted text-[12.5px]">Nothing yet.</div>}
      <ul className="space-y-3.5 text-[12.5px]">
        {activity.map((e, i) => (
          <li key={i} className="flex gap-3">
            <span className={'mt-1 w-1.5 h-1.5 rounded-full shrink-0 ' + (dotFor[e.kind] ?? 'bg-line')} />
            <div className="min-w-0">
              <div className="leading-snug"><b className="font-medium">{e.label}</b>{e.detail ? ` · ${e.detail}` : ''}</div>
              <div className="text-ink-faint mt-0.5">{relativeDate(e.at)}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
